import { getPaymentusDaysMessage } from 'app-sections/admin/settings/components/general-settings/components/paymentus-info-settings/helpers';
import PaymentTable from 'app-sections/common/applications/details/components/sections/payment/payment-table/PaymentTable';
import AuthorizationForm from 'app-sections/common/applications/summary/AuthorizationForm';
import Permits from 'app-sections/common/applications/summary/Permits';
import { useGetSettingsQuery } from 'core/api/settings/queries';
import { useApplicationsStore } from 'core/store/applications';
import { useAuthStore } from 'core/store/auth';
import { useSearchParams } from 'react-router-dom';
import { Application } from 'types';
import AppMessageOverlay from 'ui/components/app-message-overlay/AppMessageOverlay';
import LinesSkeleton from 'ui/components/skeletons/lines-skeleton/LinesSkeleton';
import FormSectionHeader from 'ui/form-section-header/FormSectionHeader';
import { isProduction } from 'ui/helpers/env';

import { Box, Card, CardContent, Typography } from '@mui/material';

import BackButton from '../components/buttons/BackButton';
import { getLinkToStep } from '../config';
import PayButtonSwoCheck from './components/pay-button-swo-check/PayButtonSwoCheck';

export interface Step3ContentProps {
  applicationData: Application;
}

export const APPLICATION_FEE = 50.0;
export const SUPPLEMENTAL_FEE = 10.0;

export default function Step3Content(props: Step3ContentProps) {
  const { applicationData } = props;
  const { activeApplicationId, hasSwoError } = useApplicationsStore();
  const { currentUser } = useAuthStore();

  const [searchParams] = useSearchParams();

  const { isLoading, isError, data: settings, error } = useGetSettingsQuery();

  const payButtonDisabled =
    ['pending', 'paid'].includes(applicationData.paymentState || '') ||
    (searchParams.get('payment_status') === 'check' && applicationData.paymentState !== 'failed') ||
    searchParams.get('payment_status') === 'success' ||
    hasSwoError;
  const paymentLocked = applicationData.paymentState === 'new';

  //  Only user that created the application can pay. It may be client or dc_registered_surveyor
  const payButtonVisible = currentUser?.id === applicationData.clientId;

  return (
    <>
      <Permits data={applicationData} />
      <AuthorizationForm data={applicationData} />
      <Box>
        <div id="payments-section">
          <FormSectionHeader title="Payment" subtitle="Verify payment details and pay fee for Wall Check." />
          <Card>
            <CardContent>
              <Typography component="h3" variant="h3">
                Payment info
              </Typography>
              <p>The application is submitted once you have paid the application/recordation fee.</p>

              {isLoading && !isError && (
                <Box my={2}>
                  <LinesSkeleton elements={2} spacing={2} isWithButtons={false} />
                </Box>
              )}

              {settings && (
                <p style={{ fontWeight: 'bold' }}>
                  Electronic check payments will take {getPaymentusDaysMessage(settings.paymentusSettings.days)} days to
                  process and Wall check Authorization form will be submitted once payment is settled.
                </p>
              )}
              <p>
                <strong>Payment fees</strong>
                <br />
                The application/recordation fee is ${APPLICATION_FEE} per lot + {SUPPLEMENTAL_FEE}% enhanced service
                fee.
                <br />
                Click on the checkout button below to proceed.
              </p>
              <p>Payment completed at:</p>
              <Box display={'flex'} flexDirection={'column'} alignItems={'flex-end'}>
                <Box
                  width={{ xs: '100%', tablet: '50%' }}
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                >
                  <PaymentTable data={applicationData} isPaid={false} />
                </Box>
              </Box>

              {payButtonVisible && (
                <>
                  {/* {(!isProduction() || searchParams.get('payment') === 'paymentus') && ( */}
                  <PayButtonSwoCheck
                    applicationId={activeApplicationId}
                    text="Pay"
                    color="primary"
                    disabled={payButtonDisabled}
                    permitRecordIds={applicationData.permitRecordIds || []}
                  />
                  {/* )} */}

                  {/* <PayStripeButtonSwoCheck
                    applicationId={activeApplicationId}
                    text="Check out and pay"
                    color="primary"
                    disabled={payButtonDisabled || paymentLocked}
                    permitRecordIds={applicationData.permitRecordIds || []}
                  /> */}

                  {isProduction() && (
                    <Box mt={1} display="flex" justifyContent="flex-end">
                      {paymentLocked && <>You have already started payment session.</>}
                    </Box>
                  )}

                  {!isProduction() && <AppMessageOverlay id="PaymentusPaymentPageInfoPopup" />}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </Box>

      {applicationData.paymentState === 'pending' && (
        <Box my={4} justifyContent="space-between">
          <BackButton isLoading={false} backButtonLink={getLinkToStep(activeApplicationId, 2)} />
        </Box>
      )}
    </>
  );
}
